// Seven-row weekly hours grid: each day has 0–4 [open, close) periods; a day
// with none is closed. Emits the structured WeeklyHours the guide computes
// open/closed from; the caller derives the diagram's display rows.

import { Plus, Trash2, Copy } from 'lucide-react';
import type { WeeklyHours, Weekday, TimeInterval } from '../../api/client';
import { WEEKDAYS, minutesToHHMM, hhmmToMinutes } from './weekly';
import { smallBtnCls } from './common';

const timeCls = 'px-1.5 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 w-[6.5rem]';

export function HoursGrid({ value, onChange, compact }: {
  value?: WeeklyHours;
  onChange: (v: WeeklyHours | undefined) => void;
  /** Tighter layout for nested-menu cards. */
  compact?: boolean;
}) {
  const weekly: WeeklyHours = value || {};

  function setDay(day: Weekday, intervals: TimeInterval[]) {
    const next: WeeklyHours = { ...weekly };
    if (intervals.length) next[day] = intervals; else delete next[day];
    onChange(Object.keys(next).length ? next : undefined);
  }
  function setInterval(day: Weekday, i: number, which: 0 | 1, raw: string) {
    const m = hhmmToMinutes(raw);
    if (m === null) return;
    const list = (weekly[day] || []).map((iv, j) => (j === i ? ((which === 0 ? [m, iv[1]] : [iv[0], m]) as TimeInterval) : iv));
    setDay(day, list);
  }
  function addInterval(day: Weekday) {
    const list = weekly[day] || [];
    if (list.length >= 4) return;
    const last = list[list.length - 1];
    const start = last ? Math.min(1380, last[1] + 60) : 540;
    setDay(day, [...list, [start, Math.min(1440, start + 480)]]);
  }
  function copyMondayToWeekdays() {
    const mon = weekly.mon || [];
    const next: WeeklyHours = { ...weekly };
    for (const d of ['tue', 'wed', 'thu', 'fri'] as Weekday[]) { if (mon.length) next[d] = mon.map(iv => [...iv] as TimeInterval); else delete next[d]; }
    onChange(Object.keys(next).length ? next : undefined);
  }

  return (
    <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
      {WEEKDAYS.map(d => {
        const list = weekly[d.key] || [];
        return (
          <div key={d.key} className="flex items-start gap-2">
            <div className={`${compact ? 'w-16' : 'w-24'} pt-1.5 text-xs font-medium text-gray-700 shrink-0`}>{compact ? d.short : d.label}</div>
            <div className="flex-1 flex flex-wrap items-center gap-1.5">
              {list.length === 0 && <span className="text-xs text-gray-400 py-1.5">Closed</span>}
              {list.map((iv, i) => (
                <span key={i} className="inline-flex items-center gap-1">
                  <input type="time" value={minutesToHHMM(iv[0])} onChange={e => setInterval(d.key, i, 0, e.target.value)} className={timeCls} />
                  <span className="text-gray-400 text-xs">–</span>
                  <input type="time" value={iv[1] >= 1440 ? '23:59' : minutesToHHMM(iv[1])} onChange={e => setInterval(d.key, i, 1, e.target.value === '23:59' ? '24:00' : e.target.value)} className={timeCls} />
                  <button type="button" onClick={() => setDay(d.key, list.filter((_, j) => j !== i))} className="p-1 text-gray-300 hover:text-red-600" title="Remove period">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
              {list.length < 4 && (
                <button type="button" onClick={() => addInterval(d.key)} className={smallBtnCls} title="Add a period"><Plus className="w-3 h-3" />{list.length ? 'period' : 'Open'}</button>
              )}
              {d.key === 'mon' && (
                <button type="button" onClick={copyMondayToWeekdays} className={`${smallBtnCls} text-gray-500 ml-auto`} title="Copy Monday to Tuesday–Friday"><Copy className="w-3 h-3" />Tue–Fri</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
